import { Logger } from '../../utils/Logger';
import { logConstants } from '../../../utils/Constants';
import urlUtils from '../../utils/urlUtils';
import { HttpHeaders, HttpRequest, HttpResponse } from '../../../utils/http/Http';
import { NativeBridgeFactory } from '../../bridge/nativeBridgeFactory';
import {
	DISCOVER_PROBE_SUFFIX,
	INDEX_SUFFIX,
	CGI_AUTH_SUFFIX,
	LABEL_TYPES,
	CREDENTIAL_TYPES,
	ACCEPT_AUTHENTICATE_RESPONSE,
} from '../../Constants';
import { AccountConfigError, AccountConfigErrorStrings } from '../../Errors';
import RoamingAccountsHelper from '../roamingAccounts/RoamingAccountsHelper';

/**
 * * Function that fetches roaming accounts response
 * @param {string} serverUrl - Store URL
 * @returns {Promise<HttpResponse>}
 */
export async function getRoamingAccountsResponse(url: string): Promise<{
	roamingAccountsResponse: HttpResponse | null;
	redirectUrlFromRoamingAccounts: string | null;
}> {
	let roamingAccountsResponse: HttpResponse | null = null;
	let redirectUrlFromRoamingAccounts: string | null = null;
	const roamingAccountsHelper = new RoamingAccountsHelper();
	try {
		roamingAccountsResponse = await roamingAccountsHelper.getRoamingAccounts(url);
		const redirectedUrl = roamingAccountsHelper.checkForHTTPRedirectURL(
			roamingAccountsResponse
		);
		if (!!redirectedUrl) {
			const urlObject = new URL(url);
			const redirectURLObject = new URL(redirectedUrl);
			urlObject.protocol = redirectURLObject.protocol;
			urlObject.host = redirectURLObject.host;
			redirectUrlFromRoamingAccounts = urlObject.toString();
			Logger.getInstance().write(
				logConstants.INFO,
				`making roaming account request using redirected url address: ${redirectUrlFromRoamingAccounts}`
			);
			roamingAccountsResponse = await roamingAccountsHelper.getRoamingAccounts(
				redirectUrlFromRoamingAccounts
			);
		}
	} catch (e) {
		//If the Auth Mechanism is User certificate authentication,
		//we get sendFailure exception on HTTP call, we need to try with legacy add account
		Logger.getInstance().write(
			logConstants.ERROR,
			`Exception in UnifiedAuthentication getRoamingAccountsResponse: ${e.message}`
		);

		if (e.message !== AccountConfigErrorStrings.ROAMING_ACCOUNTS_SEND_FAILURE) {
			throw new AccountConfigError(e.errorCode, e.message, null);
		}
	}

	return {
		roamingAccountsResponse,
		redirectUrlFromRoamingAccounts,
	};
}

/**
 * * Function that fetches discover probe response
 * @param {string} serverUrl - Store URL
 * @returns {Promise<HttpResponse>}
 */
export async function getDiscoverProbeResponse(serverUrl: URL): Promise<HttpResponse> {
	try {
		const discoverProbeUrl = new URL(
			urlUtils.addPathToUrl(serverUrl.origin, DISCOVER_PROBE_SUFFIX)
		);
		const discoverProbeRequest = new HttpRequest(
			'GET',
			discoverProbeUrl,
			new HttpHeaders({
				Connection: 'keep-alive',
				Host: discoverProbeUrl.hostname,
			}),
			'',
			true
		);
		const discoverProbeResponse = new HttpResponse(
			await NativeBridgeFactory.getBridge().fetch(discoverProbeRequest)
		);

		return discoverProbeResponse;
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			`${serverUrl.toString()}: Fetching discover probe response failed - ${e.message}`
		);
		throw new Error(AccountConfigErrorStrings.DISCOVER_PROBE_FETCH_FAILURE);
	}
}

/**
 * * Function that fetches index probe response
 * @param {string} serverUrl - Store URL
 * @returns {Promise<HttpResponse>}
 */
export async function getIndexProbeResponse(serverUrl: URL): Promise<HttpResponse> {
	try {
		const indexUrl = new URL(urlUtils.addPathToUrl(serverUrl.origin, INDEX_SUFFIX));
		const indexRequest = new HttpRequest(
			'GET',
			indexUrl,
			new HttpHeaders({
				Connection: 'Keep-Alive',
				Host: indexUrl.hostname,
			}),
			''
		);

		const indexResponse = new HttpResponse(
			await NativeBridgeFactory.getBridge().fetch(indexRequest)
		);

		return indexResponse;
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			`${serverUrl.toString()}: Fetching index probe response failed - ${e.message}`
		);
		throw new Error(AccountConfigErrorStrings.INDEX_PROBE_FETCH_FAILURE);
	}
}

/**
 * * Function that fetches cgi/auth response for gateway
 * @param {string} serverUrl - Store URL
 * @returns {Promise<HttpResponse>}
 */
export async function getCgiAuthResponse(serverUrl: URL): Promise<HttpResponse> {
	try {
		const cgiAuthRequest = new HttpRequest(
			'HEAD',
			serverUrl.origin + CGI_AUTH_SUFFIX,
			new HttpHeaders({
				Connection: 'Keep-Alive',
				Accept: ACCEPT_AUTHENTICATE_RESPONSE,
				'X-Citrix-Gateway': serverUrl.hostname,
				'X-Citrix-AM-LabelTypes': LABEL_TYPES,
				'X-Citrix-AM-CredentialTypes': CREDENTIAL_TYPES,
			}),
			''
		);

		const cgiAuthResponse = new HttpResponse(
			await NativeBridgeFactory.getBridge().fetch(cgiAuthRequest)
		);

		return cgiAuthResponse;
	} catch (e) {
		Logger.getInstance().write(
			logConstants.ERROR,
			`${serverUrl.toString()}: Fetching /cgi/authenticate response failed - ${e.message}`
		);
		throw new Error(AccountConfigErrorStrings.CGI_AUTH_FETCH_FAILURE);
	}
}
